import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIonToast } from '@ionic/react';
import { debounce } from 'lodash';

import { PlayerService } from 'services/player';
import { FavoritesReduxCommandCreator } from 'appRedux/actions/favoritesCommandCreator';
import { IListResponse } from '@interfaces/services';
import { IUserFavorite } from 'appRedux/models/playerModels';
import {
  bustFavoritesCache,
  favoritesListBucket,
  isPlayerScopedLoaded,
  playerScopedKey,
  sharedPlayerScopedGet,
} from 'lib/playerScopedListCache';

interface FavoritesHookProps {
  onOperationComplete?: () => void;
}

/** Normalize favorite/playlist EGM id for Redux (insight stores mnum as number). */
const toFavoriteSlotNumber = (slotNumber: string | number): number => {
  const n = typeof slotNumber === 'number' ? slotNumber : Number(String(slotNumber).trim());
  return Number.isFinite(n) ? n : NaN;
};

const useFavorites = ({ onOperationComplete }: FavoritesHookProps) => {
  const playerSvc = new PlayerService();
  const [presentToast] = useIonToast();
  const dispatch = useDispatch();

  const [isFavoritesLoading, setIsFavoritesLoading] = useState(false);
  const [slotFavoriteCount, setSlotFavoriteCount] = useState(0);

  const favoritesCommandCreator = FavoritesReduxCommandCreator(dispatch);

  // Once per appUserPk+casinoId (session). HotListReport remounts on
  // timeframe/insight reload must not re-hit GetUserFavorite — shared cache
  // + in-flight dedupe. Add/remove updates Redux; cache hit must NOT SetFavorites
  // or it would clobber optimistic membership.
  const fetchUserFavorites = useCallback((userId: string, casinoId: string) => {
    if (!userId || !casinoId) return;
    const key = playerScopedKey(userId, casinoId);
    const alreadyLoaded = isPlayerScopedLoaded(favoritesListBucket, key);
    if (!alreadyLoaded) setIsFavoritesLoading(true);

    const subscription = sharedPlayerScopedGet(favoritesListBucket, key, () =>
      playerSvc.GetUserFavorite(userId, casinoId)
    ).subscribe({
      next(favResponse) {
        setIsFavoritesLoading(false);
        // Cache hit: trust Redux (may already include optimistic add/remove).
        if (favResponse && !alreadyLoaded) {
          favoritesCommandCreator.SetFavorites(favResponse.DataSet);
        }
      },
      error() {
        setIsFavoritesLoading(false);
        presentToast({
          position: 'top',
          duration: 5000,
          color: 'danger',
          message: 'Failed to get Favorites list.',
        }).catch(() => null);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchSlotFavorites = useCallback(
    debounce((slotNumber: string, casinoId: string) => {
      if (!casinoId) return;
      setIsFavoritesLoading(true);

      const subscription = playerSvc.GetSlotUserFavorites(slotNumber, casinoId).subscribe({
        next(favResponse: IListResponse<IUserFavorite> | void) {
          if (favResponse) {
            setIsFavoritesLoading(false);
            setSlotFavoriteCount(favResponse.DataSet.length);
          }
        },
        error() {
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Failed to get Favorites list.',
          }).catch(() => null);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    }, 500),
    []
  );

  const handleDeleteFavorite = useCallback(
    debounce(
      (
        slotNumber: string,
        userId: string,
        casinoId: string,
        showSuccessToast = false
      ) => {
        if (!userId || !casinoId) return;
        setIsFavoritesLoading(true);
        let succeeded = false;
        const id = toFavoriteSlotNumber(slotNumber);

        const subscription = playerSvc.DeleteFavorite(slotNumber, userId, casinoId).subscribe({
          next(favResponse) {
            if (favResponse) {
              if (Number.isFinite(id)) favoritesCommandCreator.RemoveFavorite(id);
              bustFavoritesCache(userId, casinoId);
              succeeded = true;
            }
          },
          error() {
            presentToast({
              position: 'top',
              duration: 5000,
              color: 'danger',
              message: 'Failed to remove liked machine.',
            }).catch(() => null);
          },
          complete() {
            setIsFavoritesLoading(false);

            if (showSuccessToast && succeeded) {
              presentToast({
                position: 'top',
                duration: 3000,
                cssClass: 'player-linked-toast',
                message: '1 slot removed from Favorites.',
              }).catch(() => null);
            }

            onOperationComplete && onOperationComplete();
          },
        });

        return () => {
          subscription.unsubscribe();
        };
      },
      500
    ),
    []
  );

  const handleInsertFavorite = useCallback(
    debounce(
      (
        slotNumber: string,
        userId: string,
        casinoId: string,
        showSuccessToast = false
      ) => {
        if (!userId || !casinoId) return;
        setIsFavoritesLoading(true);
        let succeeded = false;
        const id = toFavoriteSlotNumber(slotNumber);

        const subscription = playerSvc.InsertFavorite(slotNumber, userId, casinoId).subscribe({
          next(favResponse) {
            if (favResponse) {
              if (Number.isFinite(id)) {
                favoritesCommandCreator.AddFavorite({
                  slotNumber: id,
                });
              }
              bustFavoritesCache(userId, casinoId);
              succeeded = true;
            }
          },
          error() {
            presentToast({
              position: 'top',
              duration: 5000,
              color: 'danger',
              message: 'Failed to Add liked slot.',
            }).catch(() => null);
          },
          complete() {
            setIsFavoritesLoading(false);

            if (showSuccessToast && succeeded) {
              presentToast({
                position: 'top',
                duration: 3000,
                cssClass: 'player-linked-toast',
                message: '1 slot added to Favorites.',
              }).catch(() => null);
            }

            onOperationComplete && onOperationComplete();
          },
        });

        return () => {
          subscription.unsubscribe();
        };
      },
      500
    ),
    []
  );

  const handleInsertFavoriteBulk = useCallback(
    (slotNumbers: string[], userId: string, casinoId: string) => {
      if (!userId || !casinoId || slotNumbers.length === 0) return;
      setIsFavoritesLoading(true);

      let remaining = slotNumbers.length;
      let added = 0;
      let failed = 0;

      const finish = () => {
        setIsFavoritesLoading(false);
        if (added > 0) bustFavoritesCache(userId, casinoId);
        if (added > 0) {
          presentToast({
            position: 'top',
            duration: 3000,
            cssClass: 'player-linked-toast',
            message:
              added === 1
                ? '1 slot added to Favorites.'
                : `${added} slots added to Favorites.`,
          }).catch(() => null);
        }
        if (failed > 0) {
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message:
              failed === 1
                ? 'Failed to add 1 slot to Favorites.'
                : `Failed to add ${failed} slots to Favorites.`,
          }).catch(() => null);
        }
        onOperationComplete && onOperationComplete();
      };

      slotNumbers.forEach((slotNumber) => {
        const id = toFavoriteSlotNumber(slotNumber);
        playerSvc.InsertFavorite(slotNumber, userId, casinoId).subscribe({
          next(response) {
            if (response) {
              if (Number.isFinite(id)) {
                favoritesCommandCreator.AddFavorite({
                  slotNumber: id,
                });
              }
              added += 1;
            }
          },
          error() {
            failed += 1;
            remaining -= 1;
            if (remaining === 0) finish();
          },
          complete() {
            remaining -= 1;
            if (remaining === 0) finish();
          },
        });
      });
    },
    []
  );

  const handleDeleteFavoriteBulk = useCallback(
    (slotNumbers: string[], userId: string, casinoId: string) => {
      if (!userId || !casinoId || slotNumbers.length === 0) return;
      setIsFavoritesLoading(true);

      let remaining = slotNumbers.length;
      let removed = 0;
      let failed = 0;
      // Collect the slots that were removed server-side and update Redux in a
      // single dispatch on completion, so a dependent list refresh fires once.
      const removedSlots: number[] = [];

      const finish = () => {
        setIsFavoritesLoading(false);
        if (removedSlots.length > 0) {
          favoritesCommandCreator.RemoveFavorites(removedSlots);
          bustFavoritesCache(userId, casinoId);
        }
        if (removed > 0) {
          presentToast({
            position: 'top',
            duration: 3000,
            cssClass: 'player-linked-toast',
            message:
              removed === 1
                ? '1 slot removed from Favorites.'
                : `${removed} slots removed from Favorites.`,
          }).catch(() => null);
        }
        if (failed > 0) {
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message:
              failed === 1
                ? 'Failed to remove 1 slot from Favorites.'
                : `Failed to remove ${failed} slots from Favorites.`,
          }).catch(() => null);
        }
        onOperationComplete && onOperationComplete();
      };

      slotNumbers.forEach((slotNumber) => {
        const id = toFavoriteSlotNumber(slotNumber);
        playerSvc.DeleteFavorite(slotNumber, userId, casinoId).subscribe({
          next(response) {
            if (response) {
              if (Number.isFinite(id)) removedSlots.push(id);
              removed += 1;
            }
          },
          error() {
            failed += 1;
            remaining -= 1;
            if (remaining === 0) finish();
          },
          complete() {
            remaining -= 1;
            if (remaining === 0) finish();
          },
        });
      });
    },
    []
  );

  return {
    slotFavoriteCount,
    isFavoritesLoading,
    onOperationComplete,
    fetchUserFavorites,
    fetchSlotFavorites,
    handleInsertFavorite,
    handleInsertFavoriteBulk,
    handleDeleteFavorite,
    handleDeleteFavoriteBulk,
  };
};

export default useFavorites;
